import React from 'react';
import { Link } from 'react-router-dom';
import { FiExternalLink, FiCheck, FiBell, FiClock, FiCalendar, FiEdit, FiDollarSign, FiPlus, FiFileText, FiX, FiMessageSquare } from 'react-icons/fi';

interface Notification {
  id: number;
  usuario_id: number;
  tipo: string;
  mensaje: string;
  url: string | null;
  leido: boolean;
  created_at: string;
  partners_source?: boolean;
  source_display?: string;
}

interface NotificationCardProps {
  notification: Notification;
  onMarkAsRead: (id: number) => void;
  onClose?: () => void;
  isMobile?: boolean;
}

// Helper function to parse service reminder messages
const parseServiceReminder = (mensaje: string) => {
  const reminderMatch = mensaje.match(/\[servicio-(\d+)-(\w+)\]/);
  if (reminderMatch) {
    const servicioId = reminderMatch[1];
    const evento = reminderMatch[2];
    const cleanMessage = mensaje.replace(/\[servicio-\d+-\w+\]/, '').trim();
    return { servicioId, evento, cleanMessage, isServiceReminder: true };
  }
  return { cleanMessage: mensaje, isServiceReminder: false };
};

// Helper function to get notification icon
const getNotificationIcon = (tipo: string, mensaje: string, isMobile: boolean = false) => {
  const { isServiceReminder, evento } = parseServiceReminder(mensaje);
  const iconSize = isMobile ? 'w-5 h-5' : 'w-4 h-4';
  
  if (isServiceReminder) {
    switch (evento) {
      case 'servicio_proximo':
        return <FiClock className={iconSize} />;
      case 'servicio_mismo_dia':
        return <FiCalendar className={iconSize} />;
      case 'servicio_cancelado':
        return <FiX className={iconSize} />;
      case 'servicio_modificado':
        return <FiEdit className={iconSize} />;
      default:
        return <FiBell className={iconSize} />;
    }
  }
  
  switch (tipo) {
    case 'recordatorio_servicio':
      return <FiBell className={iconSize} />;
    case 'factura_generada':
      return <FiDollarSign className={iconSize} />;
    case 'factura_marcada_pagada':
      return <FiCheck className={iconSize} />;
    case 'nuevo_servicio':
      return <FiPlus className={iconSize} />;
    case 'ticket_nuevo':
      return <FiMessageSquare className={iconSize} />;
    case 'ticket_respuesta':
      return <FiMessageSquare className={iconSize} />;
    default:
      return <FiFileText className={iconSize} />;
  }
};

// Helper function to generate notification link
const generateNotificationLink = (notif: Notification) => {
  // Si la notificación tiene URL específica y no es null/undefined, usar esa
  if (notif.url && notif.url !== 'null' && notif.url !== 'undefined' && notif.url !== '') {
    // Validar que sea una URL válida antes de usarla
    if (notif.url.startsWith('/')) {
      return notif.url;
    }
    // Si no empieza con /, asumir que es una URL relativa y agregar /
    return `/${notif.url}`;
  }
  
  // Extraer información del mensaje para generar URL específica
  const { isServiceReminder, servicioId } = parseServiceReminder(notif.mensaje);
  
  // Intentar extraer ID de servicio o factura del mensaje
  const serviceMatch = notif.mensaje.match(/servicio[^\d]*(\d+)/i);
  const facturaMatch = notif.mensaje.match(/factura[^\d]*(\d+)|FAC[^\d]*(\d+)/i);
  
  // Prioridad: servicio_id del parser, luego del regex, luego basado en tipo
  if (isServiceReminder && servicioId) {
    return `/servicios/${servicioId}`;
  }
  
  if (serviceMatch) {
    return `/servicios/${serviceMatch[1]}`;
  }
  
  // Manejar diferentes tipos de notificaciones
  switch (notif.tipo) {
    case 'factura_generada':
    case 'factura_marcada_pagada':
    case 'justificante_subido':
      if (facturaMatch) {
        return `/facturacion?factura=${facturaMatch[1] || facturaMatch[2]}`;
      }
      return '/facturacion';
      
    case 'nuevo_servicio':
    case 'recordatorio_servicio':
    case 'servicio': // Agregar tipo 'servicio' para notificaciones de Partners
      return '/servicios';
      
    case 'partners_sync':
      return '/configuracion/partners';
      
    case 'sistema':
      return '/configuracion';
      
    default:
      // Mejores fallbacks: si es relacionado con servicios, ir a servicios
      if (notif.mensaje.toLowerCase().includes('servicio')) {
        return '/servicios';
      }
      if (notif.mensaje.toLowerCase().includes('factura')) {
        return '/facturacion';
      }
      return '/';
  }
};

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onMarkAsRead,
  onClose,
  isMobile = false
}) => {
  const { cleanMessage, isServiceReminder, servicioId } = parseServiceReminder(notification.mensaje);
  const icon = getNotificationIcon(notification.tipo, notification.mensaje, isMobile);

  return (
    <div
      className={`${isMobile ? 'p-4' : 'p-3'} rounded-lg border transition-all ${
        notification.leido
          ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-70'
          : isServiceReminder
          ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700'
          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
      }`}
    >
      <div className={`flex items-start ${isMobile ? 'space-x-3' : 'space-x-2'}`}>
        {/* Icon */}
        <div className="flex-shrink-0 relative">
          <div className={`${isMobile ? 'w-10 h-10' : 'w-8 h-8'} ${
            notification.leido 
              ? 'bg-gray-400 dark:bg-gray-600' 
              : 'bg-gradient-to-br from-blue-500 to-blue-700'
          } rounded-full flex items-center justify-center`}>
            <span className="text-white">{icon}</span>
          </div>
          {!notification.leido && (
            <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-900 absolute -top-1 -right-1"></div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className={`${isMobile ? 'text-sm' : 'text-xs'} font-semibold ${
                notification.leido 
                  ? 'text-gray-600 dark:text-gray-400' 
                  : 'text-gray-900 dark:text-white'
              }`}>
                {isServiceReminder ? (
                  <span className="flex items-center space-x-2">
                    <span>Recordatorio</span>
                    {servicioId && (
                      <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded-full">
                        #{servicioId}
                      </span>
                    )}
                  </span>
                ) : (
                  // Mapear tipos técnicos a nombres legibles
                  notification.tipo === 'factura_marcada_pagada' ? 'Factura Pagada' :
                  notification.tipo === 'factura_generada' ? 'Nueva Factura' :
                  notification.tipo === 'nuevo_servicio' ? 'Nuevo Servicio' :
                  notification.tipo === 'recordatorio_servicio' ? 'Recordatorio' :
                  notification.tipo === 'ticket_nuevo' ? 'Nuevo Ticket' :
                  notification.tipo === 'ticket_respuesta' ? 'Respuesta Ticket' :
                  notification.tipo === 'justificante_subido' ? 'Justificante' :
                  notification.tipo
                )}
                {Boolean(notification.partners_source) && (
                  <span className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                    Partners
                  </span>
                )}
              </p>
              <p className={`${isMobile ? 'text-sm' : 'text-xs'} ${
                notification.leido 
                  ? 'text-gray-500 dark:text-gray-500' 
                  : 'text-gray-600 dark:text-gray-300'
              } mt-1 ${isMobile ? 'line-clamp-2' : 'line-clamp-3'}`}>
                {cleanMessage}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {new Date(notification.created_at).toLocaleString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit',
                  day: '2-digit',
                  month: '2-digit'
                })}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className={`flex items-center ${isMobile ? 'space-x-3 mt-3' : 'space-x-2 mt-2'}`}>
            <Link
              to={generateNotificationLink(notification)}
              onClick={onClose}
              className={`flex items-center space-x-1 ${isMobile ? 'px-3 py-1.5' : 'px-2 py-1'} text-sm ${
                notification.leido 
                  ? 'bg-gray-500 text-white hover:bg-gray-600' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              } rounded-md transition-colors`}
            >
              <FiExternalLink className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
              <span className={isMobile ? 'text-sm' : 'text-xs'}>Ver</span>
            </Link>
            {!notification.leido && (
              <button
                onClick={() => onMarkAsRead(notification.id)}
                className={`flex items-center space-x-1 ${isMobile ? 'px-3 py-1.5' : 'px-2 py-1'} text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors`}
              >
                <FiCheck className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
                <span className={isMobile ? 'text-sm' : 'text-xs'}>Leído</span>
              </button>
            )}
            {notification.leido && (
              <span className={`flex items-center space-x-1 ${isMobile ? 'px-3 py-1.5' : 'px-2 py-1'} text-sm text-gray-500 dark:text-gray-400`}>
                <FiCheck className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'}`} />
                <span className={isMobile ? 'text-sm' : 'text-xs'}>Leída</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;